"use client"

import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Capacitor } from "@capacitor/core"

import { isCapacitorNative } from "@/lib/capacitor"
import {
    closeExternalAuth,
    dispatchOAuthReturn,
    isSafeAppPath,
    parseAppOAuthUrl,
} from "@/lib/capacitor/oauth-browser"

/**
 * Native shell navigation helpers (mobile app builds only — `isCapacitorNative()`):
 *
 * - **iOS:** `capacitor-swipe-back-plugin` toggles WKWebView `allowsBackForwardNavigationGestures`
 *   so the left-edge swipe uses the browser history stack (aligned with React Router).
 * - **Android:** no system edge-swipe through the WebView; `@capacitor/app` `backButton`
 *   maps hardware / gesture-back to SPA history (`navigate(-1)`).
 * - **OAuth:** custom-scheme returns (`com.sigmai.app://oauth/...`) close the in-app
 *   browser and route back into the SPA.
 *
 * Horizontal carousels and other swiping UIs typically don’t occupy the extreme left edge,
 * so the iOS gesture remains distinguishable from in-content swiping.
 */
export function CapacitorNativeNavigation() {
    const navigate = useNavigate()

    useEffect(() => {
        if (!isCapacitorNative()) return

        let disposed = false
        const disposers: Array<() => void> = []

        const handleOAuthUrl = async (url: string) => {
            const detail = parseAppOAuthUrl(url)
            if (!detail) return

            await closeExternalAuth()
            dispatchOAuthReturn(detail)

            const defaultPath =
                detail.provider === "whoop"
                    ? "/dashboard/player/fitness"
                    : "/dashboard/player/social"
            const next =
                detail.next ||
                (detail.playerId
                    ? `${defaultPath}?id=${encodeURIComponent(detail.playerId)}${
                          detail.error ? "&error=1" : ""
                      }`
                    : null)
            if (isSafeAppPath(next) && `${window.location.pathname}${window.location.search}` !== next) {
                navigate(next)
            }
        }

        const setup = async () => {
            const { CapacitorSwipeBackPlugin } = await import("capacitor-swipe-back-plugin")
            await CapacitorSwipeBackPlugin.enable().catch(() => undefined)

            const { App } = await import("@capacitor/app")

            if (Capacitor.getPlatform() === "android") {
                const handle = await App.addListener("backButton", () => {
                    navigate(-1)
                })
                disposers.push(() => {
                    void handle.remove()
                })
            }

            const urlHandle = await App.addListener("appUrlOpen", ({ url }) => {
                void handleOAuthUrl(url)
            })
            disposers.push(() => {
                void urlHandle.remove()
            })

            const launch = await App.getLaunchUrl().catch(() => undefined)
            if (!disposed && launch?.url) {
                await handleOAuthUrl(launch.url)
            }
        }

        void setup()

        return () => {
            disposed = true
            for (const dispose of disposers) dispose()
        }
    }, [navigate])

    return null
}
